import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import type { MatChipListboxChange } from '@angular/material/chips';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import type { WaterCreateOrUpdateRequest, WaterDetail, WaterOwner } from '@models/waters';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CodeListsService } from '@services/codelists.service';
import { ConfirmationService } from '@services/confirmation.service';
import { CrmWatersService } from '@services/crm-waters.service';
import { ToastService } from '@services/toast.service';
import { BreadcrumbComponent, type BreadcrumbItem } from '@shared/components/breadcrumb/breadcrumb.component';
import { CountBadgeComponent } from '@shared/components/count-badge/count-badge.component';
import { LocalizedTextEditorComponent } from '@shared/components/localized-text-editor/localized-text-editor.component';
import { LocationPickerComponent } from '@shared/components/location-picker/location-picker.component';
import { firstValueFrom, type Observable } from 'rxjs';
import { AccommodationListComponent } from './accommodations/accommodation-list.component';
import { FishingSpotListComponent } from './fishing-spots/fishing-spot-list.component';
import { WaterInformationCardComponent } from './information/water-information-card.component';
import { WaterUserMediaBatchListComponent } from './media/water-user-media-batch-list.component';
import { WaterOwnerListComponent } from './owners/water-owner-list.component';
import { ExclusivePricingComponent } from './pricing/exclusive-pricing/exclusive-pricing.component';
import { WaterMarketFeesComponent } from './pricing/market-fees/water-market-fees.component';
import { WaterPriceListsComponent } from './pricing/price-lists/water-price-lists/water-price-lists.component';
import { SeasonPeriodsCardComponent } from './pricing/season-periods-card/season-periods-card.component';
import { WaterReviewsComponent } from './reviews/water-reviews.component';
import { CombinationPricingComponent } from './shared/combination-pricing/combination-pricing.component';
import type { DirtyParts, StageResult, WaterForm } from './water-details.model';

@Component({
  selector: 'app-water-details',
  templateUrl: './water-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatTabsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatRadioModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslatePipe,
    LocationPickerComponent,
    AccommodationListComponent,
    FishingSpotListComponent,
    WaterMarketFeesComponent,
    WaterPriceListsComponent,
    SeasonPeriodsCardComponent,
    WaterInformationCardComponent,
    WaterOwnerListComponent,
    BreadcrumbComponent,
    CountBadgeComponent,
    LocalizedTextEditorComponent,
    WaterUserMediaBatchListComponent,
    WaterReviewsComponent,
    ExclusivePricingComponent,
    CombinationPricingComponent,
  ],
})
export class WaterDetailsComponent implements OnInit {
  private readonly watersService = inject(CrmWatersService);
  readonly codeListsService = inject(CodeListsService);
  protected readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly confirmation = inject(ConfirmationService);

  readonly isEditMode = signal(false);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly isDeleting = signal(false);
  readonly waterTitle = signal('');
  readonly waterId = signal<number | null>(null);
  readonly mediaBatchCount = signal<number | null>(null);
  readonly reviewsCount = signal<number | null>(null);
  readonly selectedTabIndex = signal(0);
  protected readonly tabIndex = { fishingSpots: 2, accommodations: 3, information: 4, pricing: 5 } as const;
  readonly selectedBookable = signal<string[]>([]);
  readonly breadcrumbItems = computed<BreadcrumbItem[]>(() => [
    { label: this.translate.instant('nav.waters'), routerLink: ['/waters'] },
    {
      label:
        this.isEditMode() && this.waterTitle() ? this.waterTitle() : this.translate.instant('waters.form.create-title'),
    },
  ]);

  private backUrl: string | null = null;

  private readonly marketFees = viewChild(WaterMarketFeesComponent);

  readonly form = new FormGroup<WaterForm>({
    title: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),
    urlTitle: new FormControl(null),
    isActive: new FormControl(true, { nonNullable: true }),
    fkCountryId: new FormControl(null, [Validators.required]),
    fkExclusiveId: new FormControl(null, [Validators.required]),
    spots: new FormControl<number | null>(1, [Validators.required, Validators.min(0)]),
    coTravelerSpots: new FormControl<number | null>(0, [Validators.required, Validators.min(0)]),
    difficulty: new FormControl(1, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1), Validators.max(5)],
    }),
    waterSize: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    carpWeight: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    price: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    pricePerSpot: new FormControl(false, { nonNullable: true }),
    dogs: new FormControl(false, { nonNullable: true }),
    rowingBoat: new FormControl(null),
    baitBoat: new FormControl(null),
    restaurant: new FormControl(null),
    hasAccommodations: new FormControl(null),
    ownTent: new FormControl(null),
    ownCaravanCamper: new FormControl(null),
    carOnSpot: new FormControl(null),
    week: new FormControl(null),
    midweek: new FormControl(null),
    weekend: new FormControl(null),
    waterServicesEmail: new FormControl(null, [Validators.email]),
    mailplusList: new FormControl(null),
    latitude: new FormControl(0, { nonNullable: true }),
    longitude: new FormControl(0, { nonNullable: true }),
    voucherTextNl: new FormControl<string | null>(null),
    voucherTextEn: new FormControl<string | null>(null),
    voucherTextDe: new FormControl<string | null>(null),
    voucherTextIt: new FormControl<string | null>(null),
    voucherTextFr: new FormControl<string | null>(null),
    siteIds: new FormControl([] as number[], { nonNullable: true }),
    amenityIds: new FormControl([] as number[], { nonNullable: true }),
  });

  readonly difficultyDisplay = computed(() => `${this.form.controls.difficulty.value ?? '—'} / 5`);

  // Owner list emitted by the child list component (null until loaded).
  readonly owners = signal<WaterOwner[] | null>(null);
  private readonly servicesEmail = toSignal(this.form.controls.waterServicesEmail.valueChanges, {
    initialValue: this.form.controls.waterServicesEmail.value,
  });

  // --- Exclusive (whole-lake) pricing, on the Pricing tab (CS-286) ---

  /**
   * Whether the saved water permits whole-lake hire, and so has an exclusive price grid to edit. Comes
   * straight from the API, which derives it with the same predicate its pricing endpoints validate
   * against — the admin does not know which rental modes permit exclusive hire.
   */
  readonly canEditExclusivePrices = signal(false);

  // The saved and live rental-mode values, compared for equality only — opaque ids, never interpreted.
  private readonly savedExclusiveId = signal<number | null>(null);
  private readonly liveExclusiveId = toSignal(this.form.controls.fkExclusiveId.valueChanges, {
    initialValue: this.form.controls.fkExclusiveId.value,
  });

  /**
   * The form's rental mode no longer matches what is stored, so {@link canEditExclusivePrices} — and the
   * API's own gate — still describe the previous setting. Editing prices against a stale gate would either
   * be rejected by the API or silently apply to the wrong configuration, so the pricing section asks for
   * the water to be saved first.
   */
  readonly hasUnsavedExclusiveChange = computed(() => this.liveExclusiveId() !== this.savedExclusiveId());

  private readonly exclusivePricing = viewChild(ExclusivePricingComponent);

  // --- Raffle (shared rod places) pricing, on the Pricing tab ---

  /**
   * Whether the saved lake is sold as shared rod places rather than named swims, and so has a raffle price
   * grid to edit. Comes straight from the API, which derives it with the same predicate its raffle-prices
   * PUT validates against — the admin does not count swims and fishing-spot accommodations itself.
   *
   * Deliberately has no equivalent of {@link hasUnsavedExclusiveChange}: the answer depends on the lake's
   * swims and accommodations, which are managed on tabs of their own and cannot be edited into a stale
   * state from this form.
   */
  readonly canEditRafflePrices = signal(false);

  private readonly rafflePricing = viewChild(CombinationPricingComponent);

  /**
   * A price list was added, renamed, re-dated or deleted in the Price lists section. Both pricing grids on
   * this page have a dropdown that has to take that in — but only the dropdown: re-reading a grid here would
   * discard cell edits over a change that usually isn't even to the price list being edited.
   *
   * The per-unit combination grids need nothing from this despite being addressed by price list too. They
   * live on the swim and accommodation forms, which are routes of their own rather than parts of this page,
   * so one is never mounted while this section is on screen — and each reads the listing fresh on init, since
   * `CrmWaterPriceListsService` shares a request in flight but deliberately keeps no cache beyond it.
   */
  protected onPriceListsChanged(): void {
    this.exclusivePricing()?.priceListsChanged();
    this.rafflePricing()?.priceListsChanged();
  }

  /**
   * A pricing-section load is in flight, which withholds Save: a grid moves to the newly-selected price
   * list straight away but only replaces its cells when the GET lands, so saving in between would write the
   * previous price list's prices into the new one. The fee table is covered for the same reason — its rows
   * only exist once its GET has landed.
   */
  readonly isPricingLoading = computed(
    () =>
      this.exclusivePricing()?.isLoading() === true ||
      this.rafflePricing()?.isLoading() === true ||
      this.marketFees()?.isLoading() === true,
  );

  // Warn when a saved water has no reachable service-order email. Mirrors the priority in
  // ReservationService.OrderServicesAsync: 1) water services email, 2) primary owner's email.
  readonly missingServiceOrderEmail = computed(() => {
    if (!this.isEditMode()) return false;
    if (this.servicesEmail()?.trim()) return false;
    const owners = this.owners();
    if (owners === null) return false; // owners not loaded yet — avoid a false warning
    // Trim to match the backend's string.IsNullOrWhiteSpace check — a whitespace-only email isn't reachable.
    return !owners.some((o) => o.isPrimary && !!o.email?.trim());
  });

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    this.backUrl = this.route.snapshot.queryParamMap.get('backUrl');
    const tab = this.route.snapshot.queryParamMap.get('tab');
    this.selectedTabIndex.set(Number(tab) || 0);

    if (id) {
      const numericId = Number(id);
      this.waterId.set(numericId);
      this.isEditMode.set(true);
      this.loadWater(numericId);
    }
  }

  private loadWater(id: number): void {
    this.isLoading.set(true);
    this.watersService.getWater(id).subscribe({
      next: (water) => {
        this.patchForm(water);
        this.isLoading.set(false);
      },
      error: () => {
        this.toastService.showError(this.translate.instant('waters.form.load-failed'));
        this.isLoading.set(false);
        this.navigateBack();
      },
    });
  }

  private patchForm(water: WaterDetail): void {
    this.waterTitle.set(water.title);
    this.savedExclusiveId.set(water.fkExclusiveId);
    this.canEditExclusivePrices.set(water.permitsExclusiveHire);
    this.canEditRafflePrices.set(water.offersRaffle);
    this.form.patchValue({
      title: water.title,
      urlTitle: water.urlTitle,
      isActive: water.isActive,
      fkCountryId: water.fkCountryId,
      fkExclusiveId: water.fkExclusiveId,
      spots: water.spots,
      coTravelerSpots: water.coTravelerSpots,
      difficulty: water.difficulty ?? 1,
      waterSize: water.waterSize,
      carpWeight: water.carpWeight,
      price: water.price,
      pricePerSpot: water.pricePerSpot,
      dogs: water.dogs,
      rowingBoat: water.rowingBoat,
      baitBoat: water.baitBoat,
      restaurant: water.restaurant,
      hasAccommodations: water.hasAccommodations,
      ownTent: water.ownTent,
      ownCaravanCamper: water.ownCaravanCamper,
      carOnSpot: water.carOnSpot,
      week: water.week,
      midweek: water.midweek,
      weekend: water.weekend,
      waterServicesEmail: water.waterServicesEmail,
      mailplusList: water.mailplusList,
      latitude: parseFloat(water.latitude ?? '') || 0,
      longitude: parseFloat(water.longitude ?? '') || 0,
      voucherTextNl: water.voucherTextNl,
      voucherTextEn: water.voucherTextEn,
      voucherTextDe: water.voucherTextDe,
      voucherTextIt: water.voucherTextIt,
      voucherTextFr: water.voucherTextFr,
      siteIds: water.siteIds,
      amenityIds: water.amenityIds,
    });

    const bookable: string[] = [];
    if (water.week === true) bookable.push('week');
    if (water.midweek === true) bookable.push('midweek');
    if (water.weekend === true) bookable.push('weekend');
    this.selectedBookable.set(bookable);
  }

  onBookableChange(event: MatChipListboxChange): void {
    const val: string[] = event.value ?? [];
    this.selectedBookable.set(val);
    this.form.patchValue({
      week: val.includes('week') ? true : null,
      midweek: val.includes('midweek') ? true : null,
      weekend: val.includes('weekend') ? true : null,
    });
    // These chips aren't a form control, and `patchValue` doesn't mark anything dirty — without this the
    // stay types the lake is bookable for would be edited but never sent (see save()'s `form.dirty` gate).
    this.form.markAsDirty();
  }

  /**
   * Persist the whole screen in stages: the lake first, then the per-market fees, the exclusive pricing grid
   * and the raffle pricing grid — the same shape as the swim and accommodation forms folding in their
   * combination grid.
   *
   * The stage order is a requirement, not a reading order: a grid's gate and its axes are derived from the
   * *saved* water, so saving the lake first is what lets a newly-exclusive lake be priced, or a raffle grid
   * be filled up to a capacity raised in the same submit, in one go. Each stage also reports failure in
   * terms of what earlier stages already wrote. Do not rearrange them.
   *
   * Each part is sent only when it actually changed, and a successful save stays on the page and adopts
   * what came back rather than navigating away, so pricing can be entered in several passes. In create
   * mode the lake has no id to hang prices or fees on yet, so the freshly-created lake is routed to
   * instead (its child sections only become editable once it exists).
   */
  async save(): Promise<void> {
    const exclusivePricing = this.exclusivePricing();
    const rafflePricing = this.rafflePricing();
    const fees = this.marketFees();

    const parts = this.preflight(exclusivePricing, rafflePricing, fees);
    if (parts === null) return;

    this.isSaving.set(true);
    try {
      // Stage 1 — the lake itself.
      const lake = await this.saveLakeStage(parts.saveWater);
      if (!lake.ok) return;
      const { saved } = lake;

      // Stage 2 — the per-market fees. The section adopts the response itself, which also marks it
      // pristine, so nothing here has to reach into its state.
      const feesStage = await this.saveSectionStage(parts.saveFees ? fees?.save() : null, saved, {
        failed: 'waters.market-fees.save-failed',
        lakeSaved: 'waters.market-fees.save-failed-lake-saved',
      });
      if (!feesStage.ok) return;

      // Stage 3 — the exclusive grid, before the new water is adopted: a rental-mode change that revokes
      // exclusive hire should surface the API's explicit refusal, not silently drop the price edits.
      const pricesStage = await this.saveSectionStage(
        parts.savePrices ? exclusivePricing?.save() : null,
        saved,
        {
          failed: 'waters.exclusive-prices.save-failed',
          lakeSaved: 'waters.exclusive-prices.save-failed-lake-saved',
        },
        (error) =>
          error instanceof HttpErrorResponse && error.status === 400
            ? 'waters.exclusive-prices.save-failed-not-exclusive'
            : null,
      );
      if (!pricesStage.ok) return;

      // Stage 4 — the raffle grid, likewise before the new water is adopted. Its refusal case is a lake that
      // has gained a named swim since this page loaded, which the API states explicitly rather than letting
      // the edits vanish into a grid that no longer applies.
      const raffleStage = await this.saveSectionStage(
        parts.saveRafflePrices ? rafflePricing?.save() : null,
        saved,
        {
          failed: 'waters.raffle-prices.save-failed',
          lakeSaved: 'waters.raffle-prices.save-failed-lake-saved',
        },
        (error) =>
          error instanceof HttpErrorResponse && error.status === 400
            ? 'waters.raffle-prices.save-failed-has-spots'
            : null,
      );
      if (!raffleStage.ok) return;

      // Create mode has no page to stay on — the lake only just got an id. Move to its edit URL (replacing
      // history, so Back doesn't return to a create form for a lake that now exists), which enables the
      // child tabs and makes the next Save an update.
      if (saved !== null && !this.isEditMode()) {
        this.toastService.showSuccess(this.translate.instant('waters.form.save-success-create'));
        this.router.navigate(['/waters/edit', saved.id], {
          queryParams: this.backUrl ? { backUrl: this.backUrl } : {},
          replaceUrl: true,
        });
        return;
      }

      // Stage 5 — adopt the saved lake and re-read the pricing sections against it: enabling a stay type or
      // adding a season period grows a grid's axes, and only a reload brings the new row or column in.
      if (saved !== null) {
        this.adoptSavedWater(saved);
        exclusivePricing?.reload();
        rafflePricing?.reload();
        fees?.reload();
      }

      // Report what was actually written, rather than naming only the lake when more than the lake was
      // saved — or claiming a save at all when a section reported itself dirty but had nothing to send
      // (it can't render, or a load is still in flight).
      const key = this.saveSuccessKey(saved !== null, feesStage.sent, pricesStage.sent, raffleStage.sent);
      if (key === null) {
        this.toastService.show(this.translate.instant('waters.form.nothing-to-save'));
      } else {
        this.toastService.showSuccess(this.translate.instant(key));
      }
    } finally {
      this.isSaving.set(false);
    }
  }

  /**
   * Decide what a submit has to send, or report why it cannot proceed and answer null — nothing has been
   * written at this point, so a caller that sees null is simply done.
   */
  private preflight(
    exclusivePricing: ExclusivePricingComponent | undefined,
    rafflePricing: CombinationPricingComponent | undefined,
    fees: WaterMarketFeesComponent | undefined,
  ): DirtyParts | null {
    // Nothing touched: sending the lake back unchanged would rewrite it (and its `modified` stamp) for no
    // reason. Say so rather than flashing a success toast for a save that never happened.
    const parts: DirtyParts = {
      saveWater: !this.isEditMode() || this.form.dirty,
      saveFees: fees?.dirty() === true,
      savePrices: exclusivePricing?.dirty() === true,
      saveRafflePrices: rafflePricing?.dirty() === true,
    };
    if (!parts.saveWater && !parts.saveFees && !parts.savePrices && !parts.saveRafflePrices) {
      this.toastService.show(this.translate.instant('waters.form.nothing-to-save'));
      return null;
    }

    // Validate every part up front: a half-filled price cell or an unpaired dog fee must not leave the
    // lake saved behind it. Every child is asked unconditionally so each marks its own offending inputs,
    // rather than short-circuiting on the lake form being invalid.
    const pricesValid = exclusivePricing?.validate() ?? true;
    const raffleValid = rafflePricing?.validate() ?? true;
    const feesValid = fees?.validate() ?? true;
    if (this.form.invalid || !pricesValid || !raffleValid || !feesValid) {
      this.form.markAllAsTouched();
      this.toastService.showError(this.translate.instant('waters.form.invalid'));
      return null;
    }

    return parts;
  }

  /**
   * Send the lake, reporting its own failure. Both endpoints answer with the full detail, so what comes
   * back is also the refresh: no follow-up GET. A clean lake yields `saved: null` — the later stages read
   * that as "nothing of the lake is on the server because of this submit".
   */
  private async saveLakeStage(send: boolean): Promise<StageResult<{ saved: WaterDetail | null }>> {
    if (!send) return { ok: true, saved: null };
    try {
      return { ok: true, saved: await firstValueFrom(this.saveWaterRequest()) };
    } catch {
      this.toastService.showError(this.translate.instant('waters.form.save-failed'));
      return { ok: false };
    }
  }

  /**
   * Send one pricing section, reporting its own failure. `saved` is the lake this submit already wrote (or
   * null): on failure it is adopted, so the form stops offering to save it again and a retry sends only
   * the section, while the section keeps its edits to be fixed. `refusal` maps a rejection the API
   * explains itself onto its own message, which outranks the partial-write note it implies.
   */
  private async saveSectionStage(
    request: Observable<unknown> | null | undefined,
    saved: WaterDetail | null,
    keys: { failed: string; lakeSaved: string },
    refusal?: (error: unknown) => string | null,
  ): Promise<StageResult<{ sent: boolean }>> {
    if (!request) return { ok: true, sent: false };
    try {
      await firstValueFrom(request);
      return { ok: true, sent: true };
    } catch (error: unknown) {
      if (saved !== null) this.adoptSavedWater(saved);
      const key = refusal?.(error) ?? (saved !== null ? keys.lakeSaved : keys.failed);
      this.toastService.showError(this.translate.instant(key));
      return { ok: false };
    }
  }

  /**
   * Which success message a submit earned, or null when nothing was written. One Save can persist up to
   * four independent things, so naming only the lake would under-report a combined save.
   */
  private saveSuccessKey(
    lakeSaved: boolean,
    feesSaved: boolean,
    pricesSaved: boolean,
    raffleSaved: boolean,
  ): string | null {
    const count = Number(lakeSaved) + Number(feesSaved) + Number(pricesSaved) + Number(raffleSaved);
    if (count === 0) return null;
    if (count > 1) return lakeSaved ? 'waters.form.save-success-all' : 'waters.form.save-success-pricing';
    if (lakeSaved) return 'waters.form.save-success-edit';
    if (feesSaved) return 'waters.market-fees.save-success';
    if (pricesSaved) return 'waters.exclusive-prices.save-success';
    return 'waters.raffle-prices.save-success';
  }

  /**
   * Take a saved lake as the new truth: adopt what came back and mark the form pristine. Called both on
   * the happy path and after a later stage fails — in that case the lake is on the server regardless, and
   * leaving the form dirty would misreport what is stored and re-send it on the retry.
   */
  private adoptSavedWater(saved: WaterDetail): void {
    this.patchForm(saved);
    this.form.markAsPristine();
  }

  private saveWaterRequest(): Observable<WaterDetail> {
    const raw = this.form.getRawValue();
    const dto: WaterCreateOrUpdateRequest = {
      title: raw.title,
      urlTitle: raw.urlTitle || null,
      isActive: raw.isActive,
      fkCountryId: raw.fkCountryId,
      fkExclusiveId: raw.fkExclusiveId,
      // Both are Validators.required and preflight refuses an invalid form, so neither is null here; the
      // fallbacks only satisfy the non-nullable DTO.
      spots: raw.spots ?? 0,
      coTravelerSpots: raw.coTravelerSpots ?? 0,
      difficulty: raw.difficulty,
      waterSize: raw.waterSize,
      carpWeight: raw.carpWeight,
      price: raw.price,
      pricePerSpot: raw.pricePerSpot,
      dogs: raw.dogs,
      rowingBoat: raw.rowingBoat,
      baitBoat: raw.baitBoat,
      restaurant: raw.restaurant,
      hasAccommodations: raw.hasAccommodations,
      ownTent: raw.ownTent,
      ownCaravanCamper: raw.ownCaravanCamper,
      carOnSpot: raw.carOnSpot,
      week: raw.week,
      midweek: raw.midweek,
      weekend: raw.weekend,
      waterServicesEmail: raw.waterServicesEmail || null,
      mailplusList: raw.mailplusList ?? null,
      latitude: raw.latitude !== 0 ? raw.latitude.toFixed(6) : null,
      longitude: raw.longitude !== 0 ? raw.longitude.toFixed(6) : null,
      voucherTextNl: raw.voucherTextNl || null,
      voucherTextEn: raw.voucherTextEn || null,
      voucherTextDe: raw.voucherTextDe || null,
      voucherTextIt: raw.voucherTextIt || null,
      voucherTextFr: raw.voucherTextFr || null,
      siteIds: raw.siteIds,
      amenityIds: raw.amenityIds,
    };

    const waterId = this.waterId();
    return this.isEditMode() && waterId !== null
      ? this.watersService.updateWater(waterId, dto)
      : this.watersService.createWater(dto);
  }

  /**
   * Remove the lake, behind the same typed-name gate as a price list delete. The toolbar's delete is a
   * single icon sitting next to Cancel and Save, and it used to fire immediately — one stray click took a
   * lake, with everything hanging off it, out of the CRM and off the website. The API soft-deletes, so this
   * is recoverable, but only by someone with database access; that is not an "undo" a CRM user has.
   */
  async delete(): Promise<void> {
    const id = this.waterId();
    if (!id) return;

    const confirmed = await this.confirmation.open({
      title: this.translate.instant('waters.form.delete-title'),
      prompt: this.translate.instant('waters.form.delete-prompt', { name: this.waterTitle() }),
      requirePhrase: this.waterTitle(),
    });
    if (!confirmed) return;

    this.isDeleting.set(true);
    this.watersService.deleteWater(id).subscribe({
      next: () => {
        this.toastService.showSuccess(this.translate.instant('waters.form.delete-success'));
        this.isDeleting.set(false);
        this.router.navigate(['/waters']);
      },
      error: () => {
        this.toastService.showError(this.translate.instant('waters.form.delete-failed'));
        this.isDeleting.set(false);
      },
    });
  }

  cancel(): void {
    this.navigateBack();
  }

  private navigateBack(): void {
    if (this.backUrl) {
      this.router.navigateByUrl(this.backUrl);
    } else {
      this.router.navigate(['/waters']);
    }
  }
}
